import React, { useEffect, useRef, useState } from 'react';
import { MainTracker } from '../../../../PixelTrackers/MainTracker';
import { useShippingPageContext } from '../../../v2/ShippingV2/providers/ShippingPageProvider';
import { translate } from '../../../../utils/translator';
import './RxPremiumUpsellPopup.css';

export type RxUpsellVariant = 'DE' | 'FR_CH';

interface RxPremiumUpsellPopupProps {
  variant: RxUpsellVariant;
  onAccept: () => void;
  onDecline: () => void;
  onClose: () => void;
}

const FEATURE_ROWS: Record<RxUpsellVariant, string[]> = {
  DE: ['row_waitTime', 'row_stockGuarantee', 'row_noPharmacy', 'row_fastTrack', 'row_vipSupport'],
  FR_CH: ['row_noPharmacy', 'row_stockGuarantee', 'row_fastTrack', 'row_vipSupport'],
};

const POPUP_VARIANT_LABELS: Record<RxUpsellVariant, string> = {
  DE: 'Germany',
  FR_CH: 'FR-CH',
};

const CheckIcon = () => (
  <svg width="18" height="18" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.5 7L5.5 10L11.5 4" stroke="#269C6E" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CrossIcon = () => (
  <svg width="18" height="18" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 4L10 10M10 4L4 10" stroke="#e88080" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);

const CloseIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 1L11 11M11 1L1 11" stroke="#888" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>
);

const RxPremiumUpsellPopup: React.FC<RxPremiumUpsellPopupProps> = ({ variant, onAccept, onDecline, onClose }) => {
  const { country, contentType, language } = useShippingPageContext();

  const t = (key: string) => translate(language, 'shipping', `RxPremiumUpsellPopup/${key}`) as string;
  const isCHDE = variant === 'FR_CH' && language === 'de';

  const popupRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    const update = () => {
      if (!popupRef.current) return;
      const maxH = window.innerHeight - 20;
      const h = popupRef.current.scrollHeight;
      setScale(h > maxH ? maxH / h : 1);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const categoryFromContentType = contentType || 'Other';
  const popupVariantLabel = POPUP_VARIANT_LABELS[variant];

  const eventsTracking = (window as any).eventsTracking;
  const user_type = (eventsTracking?.getData?.('token') && eventsTracking?.getData?.('last_order')) ? 'RP' : 'FTB';

  useEffect(() => {
    MainTracker.track('custom', 'Rx Premium Upsell', {
      site: country,
      category: categoryFromContentType,
      popup_variant: popupVariantLabel,
      user_type,
      upsell_rx_conversion: 'No',
    });
  }, []);

  const handleAccept = () => {
    MainTracker.track('custom', 'Upgrade Accepted', {
      site: country,
      category: categoryFromContentType,
      popup_variant: popupVariantLabel,
      user_type,
      upsell_rx_conversion: 'Yes',
    });
    onAccept();
  };

  return (
    <div className="fixed inset-0 bg-slate-500/75 z-[1000] flex items-center justify-center">
      <div ref={popupRef} className="w-[95vw] max-w-[480px] md:max-w-[620px] rounded-[12px] animate-fade-in-up md:animate-fade-in-up-10 flex flex-col" style={scale < 1 ? { zoom: scale } : undefined}>

        {/* "GO TO PREMIUM" tab above card */}
        <div
          className="mx-auto w-[200px] md:w-[270px] h-[30px] md:h-[38px] rounded-tl-[12px] rounded-tr-[12px] flex items-center justify-center linear-background-color-primary"
        >
          <span className="text-white text-[12px] md:text-[16px] font-poppins font-medium uppercase tracking-[0.7px]">
            {t('topBanner')}
          </span>
        </div>

        {/* Main card */}
        <div
          className="bg-[#f3f4f7] border-[0.728px] border-[rgba(255,255,255,0.1)] rounded-[12px] overflow-hidden relative rx-popup-card-shadow"
        >
          {/* Close button — outside scroll container so it stays fixed */}
          <button
            onClick={onClose}
            className="absolute top-[10px] right-[10px] p-[4px] z-10 border-0 bg-transparent cursor-pointer"
            aria-label="Close"
          >
            <CloseIcon />
          </button>

          {/* Header */}
          <div className="px-5 md:px-7 pt-5 md:pt-6 pb-2 md:pb-3 text-center">
            <h2 className="font-poppins font-medium text-[20px] md:text-[27px] text-black leading-[1.2]">
              {t(isCHDE ? 'title_ch' : 'title')}
            </h2>
            <p className="text-[#555] text-[12px] md:text-[16px] mt-2 leading-[1.2]">
              {t(isCHDE ? 'subtitle_ch' : 'subtitle')}
            </p>
          </div>

          {/* Comparison table section */}
          <div className="px-[14px] md:px-[18px] pb-2 md:pb-4 relative">
            {/* Gradient border for Premium column — all 4 sides, transparent interior via CSS mask */}
            <div
              className="absolute pointer-events-none z-10 top-0 bottom-2 md:bottom-4 right-[89px] md:right-[178px] w-[75px] md:w-[160px] rx-popup-gradient-border"
            />
            <div className="grid grid-cols-[minmax(0,1fr)_75px_75px] md:grid-cols-[minmax(0,1fr)_160px_160px]">

              {/* === Row A: gradient badge — full column width, text capped to force 3-line wrap === */}
              <div />
              <div
                className="rounded-t-[7px] flex items-center justify-center py-[6px] bg-[#e1b95a]"
              >
                <p className="text-white text-[9px] md:text-[15px] font-poppins font-semibold text-center leading-[10px] md:leading-[17px] max-w-[62px] md:max-w-[95px] px-[2px]">
                  {t('popularBadge')}
                </p>
              </div>
              <div />

              {/* === Row B: column titles only === */}
              <div />
              <div
                className="bg-white flex items-center justify-center pt-[8px] md:pt-[12px] pb-[3px] md:pb-[5px] px-[3px] rx-popup-premium-shadow"
              >
                <p className="font-roboto font-semibold text-[11px] md:text-[19px] text-[#18181b] text-center">
                  {t('premiumLabel')}
                </p>
              </div>
              <div
                className="bg-[#f3f4f7] border-l border-r border-t border-white rounded-t-[9px] flex items-center justify-center pt-[6px] pb-[3px] rx-popup-prescription-shadow"
              >
                <p className="font-roboto font-semibold text-[11px] md:text-[19px] text-[#18181b] text-center leading-[1.12] w-full px-[2px]">
                  {t('prescriptionLabel')}
                </p>
              </div>

              {/* === Row C: "WHAT'S INCLUDED" label + delivery badges — same row = same height === */}
              <div className="flex items-center font-poppins font-medium text-[12px] text-[#7a7e8a] uppercase tracking-[1px] py-[4px] pr-1">
                {t('whatsIncluded')}
              </div>
              <div className="bg-white flex items-center justify-center">
                <div className="w-full mx-[3px] min-h-[34px] md:min-h-[44px] py-[4px] flex items-center justify-center bg-[rgba(222,185,90,0.26)]">
                  <p className="font-roboto font-medium italic text-[12px] md:text-[15px] text-[#c7a246] text-center leading-tight w-full">
                    {t('premiumDelivery')}
                  </p>
                </div>
              </div>
              <div className="bg-[#f3f4f7] flex items-center justify-center">
                <div className="w-full mx-[0.18px] min-h-[34px] md:min-h-[44px] py-[4px] bg-[#dbdee7] flex items-center justify-center">
                  <p className="font-roboto font-medium italic text-[10px] md:text-[12px] text-[#646464] text-center leading-tight w-full">
                    {t('prescriptionDelivery')}
                  </p>
                </div>
              </div>

              {/* === Feature rows — row_waitTime renders wait time text; others render check/cross icons === */}
              {FEATURE_ROWS[variant].map((rowKey) => {
                if (rowKey === 'row_waitTime') {
                  return (
                    <React.Fragment key={rowKey}>
                      <div className="flex items-center font-roboto font-normal text-[13px] md:text-[20px] text-black py-[10px] md:py-[14px] pr-1 border-b border-black/5 break-words">
                        {t(rowKey)}
                      </div>
                      <div className="bg-white flex items-center justify-center border-b border-white/40 px-[3px]">
                        <p className="text-[12px] md:text-[15px] text-black text-center leading-[1.4]">
                          <span className="font-roboto font-semibold">{'1-2 '}</span>
                          <span className="font-roboto font-normal">{t('premiumWaitUnit')}</span>
                        </p>
                      </div>
                      <div className="bg-[#f3f4f7] flex items-center justify-center border-b border-black/5">
                        <p className="text-[12px] md:text-[15px] text-black text-center leading-[1.4]">
                          <span className="font-roboto font-normal">{t('prescriptionWaitPrefix') + ' '}</span>
                          <span className="font-roboto font-semibold">{'14 '}</span>
                          <span className="font-roboto font-normal">{t('prescriptionWaitUnit')}</span>
                        </p>
                      </div>
                    </React.Fragment>
                  );
                }
                return (
                  <React.Fragment key={rowKey}>
                    <div className="flex items-center font-roboto font-normal text-[13px] md:text-[20px] text-black py-[10px] md:py-[14px] pr-1 border-b border-black/5 break-words">
                      {t(rowKey)}
                    </div>
                    <div className="bg-white flex items-center justify-center border-b border-white/40">
                      <CheckIcon />
                    </div>
                    <div className="bg-[#f3f4f7] flex items-center justify-center border-b border-black/5">
                      <CrossIcon />
                    </div>
                  </React.Fragment>
                );
              })}

              {/* === Fee row === */}
              <div className="flex items-center font-roboto font-normal text-[13px] md:text-[20px] text-black py-[10px] md:py-[14px] pr-1">
                {t('row_prescriptionFee')}
              </div>
              <div className="bg-[#EFFFDA] flex items-center justify-center rounded-b-[6px] min-h-[48px] md:min-h-[62px] px-[3px]">
                <span className="font-poppins font-semibold italic text-[14px] md:text-[21px] text-[#7cb253] text-center leading-tight">
                  {t('feeDiscount')}
                </span>
              </div>
              <div className="bg-[#f3f4f7] flex items-center justify-center rounded-b-[9px] min-h-[48px] md:min-h-[62px]">
                <p className="font-poppins text-[10px] md:text-[14px] text-[#5a5a5b] text-center">{t('feeFullPrice')}</p>
              </div>

            </div>
          </div>

          {/* CTA button */}
          <div className="px-6 md:px-8 pt-2 md:pt-3 pb-3 md:pb-5 flex justify-center">
            <button
              onClick={handleAccept}
              className="h-[50px] md:h-[64px] w-full rounded-[12px] text-white font-poppins font-medium text-[15px] md:text-[20px] border-0 cursor-pointer linear-background-color-primary"
            >
              {t('cta')}
            </button>
          </div>

          {/* Decline link */}
          <div className="text-center rx-popup-safe-area-pb">
            <button
              onClick={onDecline}
              className="font-roboto font-medium text-[13px] md:text-[17px] text-[#686876] underline border-0 bg-transparent outline-none cursor-pointer"
            >
              {t('decline')}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default RxPremiumUpsellPopup;
